import { createContext } from 'react';
import Api from 'api';
import { getCountryCode } from 'utils/localisation';
import { getEntryPointsFromStorages, getReferrer } from 'utils';
import { showModal } from 'components/Modals';
import { phoneCode } from 'define/locale';
import { nonCorporateEmails } from './constatnts';

const handleFormError = async (data, errorMessage) => {
  const payload = { ...data, errorMessage };

  if (data?.calculatorData) {
    payload.calculatorData = Object.values(data?.calculatorData)
      .map(item => {
        if (item.data) {
          return `${item.label}: ${Object.values(item.data)
            .map(subitem => {
              if (typeof subitem.value === 'number') {
                return `${subitem.label}: ${subitem.value}`;
              }
              return subitem.label;
            })
            .join(', ')}`;
        }

        return `${item.label}: ${item.value}`;
      })
      .join('; ');
  }

  await fetch(`${process.env.GATSBY_GOOGLE_SCRIPT_URL}?action=addRow`, {
    method: 'POST',
    body: JSON.stringify(payload),
    redirect: 'follow',
    mode: 'no-cors',
  });
};

export const createInitialValues = fields => {
  const result = {};

  for (const fieldKey in fields) {
    if (fields[fieldKey].initialValue === undefined) {
      throw new TypeError(
        `The settings object of field "${fieldKey}" must contain "initialValue" property!`,
      );
    }

    result[fieldKey] = fields[fieldKey].initialValue;
  }

  return result;
};

export const createFields = (fields, nda) => {
  const result = {};
  const fieldsArray = Object.entries(fields);

  for (let i = 0; i < fieldsArray.length; i++) {
    result[fieldsArray[i][0]] = {
      ...fieldsArray[i][1],
      name: fieldsArray[i][0],
      order: i,
    };
  }

  if (nda) {
    result.nda = {
      name: 'nda',
      initialValue: Boolean(nda.initialValue),
      top: Boolean(nda.top),
      order: fieldsArray.length,
    };
  }

  return result;
};

export const submitHandler = async ({
  id,
  formName,
  values,
  handler,
  onSuccess,
  successModal,
  errorModal,
  onError,
  onResponse,
  reset: { reset, resetHandler },
  setStatus,
  additionalFormData,
  getAdditionalFormData,
  pushSubmitEvent,
}) => {
  const gclid = window.localStorage.getItem('gclid');
  const countryCode = getCountryCode() ?? 'No country code';

  let data = {
    form: formName,
    ...values,
    ...additionalFormData,
    ...getEntryPointsFromStorages(),
    lastPoint: window.location.href,
    referrer: getReferrer(),
    gclid,
    countryCode,
    captchaToken: await Api.getCaptchaToken(),
  };

  if (typeof getAdditionalFormData === 'function') {
    data = { ...data, ...getAdditionalFormData() };
  }

  try {
    await handler(data);

    if (typeof onSuccess === 'function') {
      onSuccess();
    }

    if (typeof onResponse === 'function') {
      onResponse();
    }

    if (typeof pushSubmitEvent === 'function') {
      pushSubmitEvent(formName);
    }

    if (successModal) {
      showModal(successModal);
    }

    if (reset) {
      resetHandler();
    }

    setStatus('success');
  } catch (e) {
    if (typeof onError === 'function') {
      onError();
    }

    if (typeof onResponse === 'function') {
      onResponse();
    }

    if (errorModal) {
      showModal(errorModal);
    }

    if (process.env.NODE_ENV !== 'development') {
      await handleFormError(data, e.message);
    }

    setStatus();
  }
};

export const findFirstFieldWithError = (fields, errors) => {
  let min = null;
  let targetFieldName = null;
  const fieldsWithError = Object.keys(errors);

  for (const fieldKey in fields) {
    if (fieldsWithError.includes(fieldKey)) {
      if (min === null || min > fields[fieldKey].order) {
        min = fields[fieldKey].order;
        targetFieldName = fieldKey;
      }
    }
  }

  return targetFieldName;
};

export const filterValidationRules = rules => {
  const newRules = {};

  for (const rulesKey in rules) {
    if (document.querySelector(`[name=${rulesKey}]`)) {
      newRules[rulesKey] = rules[rulesKey];
    }
  }

  return newRules;
};

export const getShortFileName = fileName => {
  if (fileName.length > 4) {
    const fileFormat = fileName.substring(fileName.lastIndexOf('.'));
    return `${fileName?.slice(0, 4)}...${fileFormat}`;
  }

  return fileName;
};

export const calculateFileSizeTitle = bytes => {
  const megabytes = bytes / (1024 * 1024);
  return `${Math.round(megabytes * 100) / 100}MB`;
};

export const ErrorsShakeHubContext = createContext();
export const ThemeContext = createContext();

export const getInitialPhoneValue = () => {
  if (typeof window !== 'undefined') {
    const country = getCountryCode()?.toLowerCase();
    return phoneCode[country] ?? '+';
  }
  return '+';
};

export const corporateValidation = value => {
  if (!/@.+\./.test(value)) return true;

  return !nonCorporateEmails.some(hostname => value.includes(hostname));
};
